import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Responsive,
  type Layout,
  type LayoutItem,
  type ResponsiveLayouts,
  verticalCompactor,
} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DashboardBlockCard } from './DashboardBlockCard';
import type { DashboardBlock } from '@pm-next/shared-types';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

const ROW_HEIGHT = 40;
const MARGIN: [number, number] = [12, 12];
/** Floor height so compact text/list blocks don't collapse. */
export const MIN_BLOCK_H = 5;
const DRAG_CANCEL =
  'input,textarea,button,select,a,label,[role="combobox"],[role="option"],[data-radix-collection-item],[data-no-dnd]';

/** Container-width breakpoints (not viewport) so the AI sidebar is accounted for. */
const BREAKPOINTS = { lg: 900, md: 600, sm: 0 } as const;
const COLS = { lg: 12, md: 6, sm: 1 } as const;
type Bp = keyof typeof BREAKPOINTS;

interface DashboardGridProps {
  dashboardId: string;
  blocks: DashboardBlock[];
  editMode: boolean;
  canEdit: boolean;
  width: number;
  onLayoutChange: (layout: Layout) => void;
}

function pxToGridH(px: number): number {
  return Math.max(MIN_BLOCK_H, Math.ceil((px + MARGIN[1]) / (ROW_HEIGHT + MARGIN[1])));
}

function overlapsX(a: LayoutItem, b: LayoutItem): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x;
}

/**
 * Group items into visual rows (side-by-side = same row), equalize height to the
 * tallest natural height in each row, then stack rows without gaps.
 *
 * Uses X-collision packing instead of exact `y` matching so later rows still
 * equalize when saved/equalized y values drift apart.
 */
export function applyEqualRowHeights(
  items: LayoutItem[],
  naturalH: Record<string, number>
): LayoutItem[] {
  if (items.length === 0) return items;

  const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  const rows: LayoutItem[][] = [];

  for (const item of sorted) {
    const last = rows[rows.length - 1];
    if (!last) {
      rows.push([item]);
      continue;
    }
    // If this item overlaps any block already in the current row, it belongs below.
    if (last.some((r) => overlapsX(r, item))) {
      rows.push([item]);
    } else {
      last.push(item);
    }
  }

  let nextY = 0;
  const out: LayoutItem[] = [];
  for (const row of rows) {
    const rowH = Math.max(MIN_BLOCK_H, ...row.map((i) => naturalH[i.i] ?? i.h));
    for (const item of [...row].sort((a, b) => a.x - b.x)) {
      out.push({ ...item, y: nextY, h: rowH, minH: MIN_BLOCK_H });
    }
    nextY += rowH;
  }
  return out;
}

/** Pack items left-to-right, wrapping when a row overflows `cols`. */
function reflowToCols(
  items: LayoutItem[],
  cols: number,
  naturalH: Record<string, number>
): LayoutItem[] {
  const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  let x = 0;
  let y = 0;
  let rowH = 0;
  const packed: LayoutItem[] = [];

  for (const item of sorted) {
    const h = naturalH[item.i] ?? item.h;
    const w = Math.min(cols, Math.max(1, Math.round((item.w / 12) * cols)));

    if (x + w > cols) {
      x = 0;
      y += rowH;
      rowH = 0;
    }

    packed.push({
      ...item,
      x,
      y,
      w,
      h: Math.max(MIN_BLOCK_H, h),
      minW: 1,
      minH: MIN_BLOCK_H,
    });

    x += w;
    rowH = Math.max(rowH, h);
    if (x >= cols) {
      x = 0;
      y += rowH;
      rowH = 0;
    }
  }

  return applyEqualRowHeights(packed, naturalH);
}

function blocksToLgLayout(
  blocks: DashboardBlock[],
  naturalH: Record<string, number>,
  prev?: Layout
): LayoutItem[] {
  const prevById = new Map((prev ?? []).map((item) => [item.i, item]));
  const base = blocks.map((b) => {
    const previous = prevById.get(b.id);
    return {
      i: b.id,
      // Prefer last local layout (after drag / equalize), else saved block coords.
      x: previous?.x ?? b.x,
      y: previous?.y ?? b.y,
      w: b.w,
      h: naturalH[b.id] ?? previous?.h ?? Math.max(b.h, MIN_BLOCK_H),
      minW: 1,
      minH: MIN_BLOCK_H,
    };
  });
  return applyEqualRowHeights(base, naturalH);
}

function buildLayouts(
  blocks: DashboardBlock[],
  naturalH: Record<string, number>,
  prevLg?: Layout
): ResponsiveLayouts<Bp> {
  const lg = blocksToLgLayout(blocks, naturalH, prevLg);
  return {
    lg,
    md: reflowToCols(lg, COLS.md, naturalH),
    sm: reflowToCols(lg, COLS.sm, naturalH),
  };
}

function GridItemShell({
  block,
  dashboardId,
  editMode,
  canEdit,
  onHeightPx,
}: {
  block: DashboardBlock;
  dashboardId: string;
  editMode: boolean;
  canEdit: boolean;
  onHeightPx: (blockId: string, heightPx: number) => void;
}) {
  const shellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;

    const report = () => {
      const measureEl = el.querySelector<HTMLElement>('[data-dashboard-block-measure]');
      const chromeEl = el.querySelector<HTMLElement>('[data-dashboard-block-measure-chrome]');
      const chromePx = chromeEl ? chromeEl.offsetHeight + 4 /* mb-1 */ : 0;
      const contentPx = (measureEl?.offsetHeight ?? el.scrollHeight) + chromePx;
      onHeightPx(block.id, contentPx);
    };
    report();
    const ro = new ResizeObserver(() => requestAnimationFrame(report));
    const measureEl = el.querySelector<HTMLElement>('[data-dashboard-block-measure]');
    if (measureEl) ro.observe(measureEl);
    const chromeEl = el.querySelector<HTMLElement>('[data-dashboard-block-measure-chrome]');
    if (chromeEl) ro.observe(chromeEl);
    return () => ro.disconnect();
  }, [block.id, onHeightPx, editMode, block.w, block.title, block.config?.ui?.hideFilters]);

  return (
    <div ref={shellRef} className="dashboard-grid-item-shell flex h-full w-full flex-col">
      {editMode && canEdit && (
        <div
          data-dashboard-block-measure-chrome
          className={cn(
            'dashboard-block-drag mb-1 flex h-8 shrink-0 cursor-grab items-center gap-1.5',
            'select-none rounded border border-dashed bg-muted/70 px-2 text-xs text-muted-foreground',
            'active:cursor-grabbing'
          )}
          style={{ WebkitUserSelect: 'none', userSelect: 'none', touchAction: 'none' }}
          onMouseDown={(e) => e.preventDefault()}
          title="Drag to reposition"
        >
          <GripVertical className="pointer-events-none h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="pointer-events-none select-none">Drag to reposition</span>
        </div>
      )}
      <div className="min-h-0 flex-1">
        <DashboardBlockCard
          dashboardId={dashboardId}
          block={block}
          editMode={editMode}
          canEdit={canEdit}
        />
      </div>
    </div>
  );
}

export function DashboardGrid({
  dashboardId,
  blocks,
  editMode,
  canEdit,
  width,
  onLayoutChange,
}: DashboardGridProps) {
  const [naturalH, setNaturalH] = useState<Record<string, number>>({});
  const [layouts, setLayouts] = useState<ResponsiveLayouts<Bp>>(() => buildLayouts(blocks, {}));
  const [breakpoint, setBreakpoint] = useState<Bp>('lg');
  const draggingRef = useRef(false);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const naturalHRef = useRef(naturalH);
  naturalHRef.current = naturalH;
  const breakpointRef = useRef(breakpoint);
  breakpointRef.current = breakpoint;

  const handleHeightPx = useCallback((blockId: string, heightPx: number) => {
    const nextH = pxToGridH(heightPx);
    setNaturalH((prev) => (prev[blockId] === nextH ? prev : { ...prev, [blockId]: nextH }));
  }, []);

  useEffect(() => {
    if (draggingRef.current) return;
    setLayouts((prev) => buildLayouts(blocks, naturalH, prev.lg));
  }, [blocks, naturalH]);

  // Drag/reposition only on the desktop layout — smaller breakpoints are derived wraps.
  const allowDrag = editMode && canEdit && breakpoint === 'lg';

  if (blocks.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
        No blocks yet. Add one from the catalog or with a prompt.
      </div>
    );
  }

  return (
    <div
      className={cn(
        'dashboard-grid [&_.react-grid-item]:!overflow-visible',
        allowDrag && 'select-none'
      )}
    >
      {editMode && canEdit && breakpoint !== 'lg' && (
        <p className="mb-2 text-xs text-muted-foreground">
          Layout editing is available on larger screens. Blocks wrap automatically here.
        </p>
      )}
      <Responsive
        className="layout"
        width={Math.max(width, 280)}
        layouts={layouts}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        margin={MARGIN}
        dragConfig={{
          enabled: allowDrag,
          handle: '.dashboard-block-drag',
          cancel: DRAG_CANCEL,
          threshold: 3,
        }}
        resizeConfig={{ enabled: false }}
        compactor={verticalCompactor}
        onBreakpointChange={(bp) => setBreakpoint(bp as Bp)}
        onDragStart={() => {
          draggingRef.current = true;
        }}
        onDragStop={(next) => {
          draggingRef.current = false;
          if (breakpointRef.current !== 'lg') return;

          const merged = next.map((item) => {
            const block = blocksRef.current.find((b) => b.id === item.i);
            return {
              ...item,
              w: block?.w ?? item.w,
              h: Math.max(MIN_BLOCK_H, naturalHRef.current[item.i] ?? item.h),
              minW: 1,
              minH: MIN_BLOCK_H,
            };
          });
          const equalized = applyEqualRowHeights(merged, naturalHRef.current);
          setLayouts({
            lg: equalized,
            md: reflowToCols(equalized, COLS.md, naturalHRef.current),
            sm: reflowToCols(equalized, COLS.sm, naturalHRef.current),
          });
          onLayoutChange(equalized);
        }}
      >
        {blocks.map((block) => (
          <div key={block.id} className="dashboard-grid-item h-full">
            <GridItemShell
              block={block}
              dashboardId={dashboardId}
              editMode={editMode}
              canEdit={canEdit}
              onHeightPx={handleHeightPx}
            />
          </div>
        ))}
      </Responsive>
    </div>
  );
}
